import type { ComponentProps, ReactNode } from 'react'

import { Dialog as SheetPrimitive } from '@base-ui/react/dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export const Sheet = (props: SheetPrimitive.Root.Props): ReactNode => {
    return <SheetPrimitive.Root data-slot='sheet' {...props} />
}

export const SheetTrigger = (props: SheetPrimitive.Trigger.Props): ReactNode => {
    return <SheetPrimitive.Trigger data-slot='sheet-trigger' {...props} />
}

export const SheetClose = (props: SheetPrimitive.Close.Props): ReactNode => {
    return <SheetPrimitive.Close data-slot='sheet-close' {...props} />
}

export const SheetPortal = (props: SheetPrimitive.Portal.Props): ReactNode => {
    return <SheetPrimitive.Portal data-slot='sheet-portal' {...props} />
}

export const SheetOverlay = ({ className, ...props }: SheetPrimitive.Backdrop.Props): ReactNode => {
    return (
        <SheetPrimitive.Backdrop
            data-slot='sheet-overlay'
            className={cn(
                'fixed inset-0 z-50 bg-ink/40 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-[2px]',
                className
            )}
            {...props}
        />
    )
}

type SheetContentProps = SheetPrimitive.Popup.Props & {
    side?: 'top' | 'right' | 'bottom' | 'left'
    showCloseButton?: boolean
}

export const SheetContent = ({
    className,
    children,
    side = 'right',
    showCloseButton = true,
    ...props
}: SheetContentProps): ReactNode => {
    return (
        <SheetPortal>
            <SheetOverlay />

            <SheetPrimitive.Popup
                data-slot='sheet-content'
                data-side={side}
                className={cn(
                    'fixed z-50 flex flex-col gap-4 bg-popover text-sm text-popover-foreground shadow-2xl transition duration-250 ease-out outline-none data-ending-style:opacity-0 data-starting-style:opacity-0',
                    'data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-[86%] data-[side=right]:max-w-sm data-[side=right]:data-ending-style:translate-x-10 data-[side=right]:data-starting-style:translate-x-10',
                    'data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-[86%] data-[side=left]:max-w-sm data-[side=left]:data-ending-style:-translate-x-10 data-[side=left]:data-starting-style:-translate-x-10',
                    'data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:data-ending-style:-translate-y-10 data-[side=top]:data-starting-style:-translate-y-10',
                    'data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:data-ending-style:translate-y-10 data-[side=bottom]:data-starting-style:translate-y-10',
                    className
                )}
                {...props}
            >
                {children}

                {showCloseButton && (
                    <SheetPrimitive.Close
                        data-slot='sheet-close'
                        className='absolute top-4 right-4 inline-flex size-10 items-center justify-center rounded-full text-muted-foreground transition-colors outline-none hover:bg-muted hover:text-ink focus-visible:ring-4 focus-visible:ring-ring/30'
                    >
                        <X className='size-5' aria-hidden='true' />
                        <span className='sr-only'>
                            Tutup menu
                        </span>
                    </SheetPrimitive.Close>
                )}
            </SheetPrimitive.Popup>
        </SheetPortal>
    )
}

export const SheetHeader = ({ className, ...props }: ComponentProps<'div'>): ReactNode => {
    return (
        <div
            data-slot='sheet-header'
            className={cn('flex flex-col gap-0.5 p-5', className)}
            {...props}
        />
    )
}

export const SheetFooter = ({ className, ...props }: ComponentProps<'div'>): ReactNode => {
    return (
        <div
            data-slot='sheet-footer'
            className={cn('mt-auto flex flex-col gap-2 p-5', className)}
            {...props}
        />
    )
}

export const SheetTitle = ({ className, ...props }: SheetPrimitive.Title.Props): ReactNode => {
    return (
        <SheetPrimitive.Title
            data-slot='sheet-title'
            className={cn('font-heading text-base font-semibold text-ink', className)}
            {...props}
        />
    )
}

export const SheetDescription = ({ className, ...props }: SheetPrimitive.Description.Props): ReactNode => {
    return (
        <SheetPrimitive.Description
            data-slot='sheet-description'
            className={cn('text-sm text-muted-foreground', className)}
            {...props}
        />
    )
}
